
    import { Component, Vue, Prop, Watch } from 'vue-facing-decorator';
    import { Action, Getter } from '@/plugins/vuex-decorators';

    interface FilterRow {
        areaPropertyName: string | null;
        minValue: number | null;
        maxValue: number | null;
    }

    const namespace = 'main';

    @Component({
        emits: ['cancel', 'save'],
    })
    export default class ExportAreasDialog extends Vue {
        @Prop({ default: null }) public exportAreasDialog: any;
        @Prop({ default: null }) public model: any;

        @Getter('getExportAreasProperties', { namespace }) getExportAreasProperties!: any[];
        @Action('fetchExportAreasProperties', { namespace }) fetchExportAreasProperties!: any;
        @Action('previewExportAreasCount', { namespace }) previewExportAreasCount!: any;

        private modalShow = false;
        private areaSetId: number | null = null;
        private filters: FilterRow[] = [];
        private loadingProperties = false;
        private includeValues = false;

        private mounted() {
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape") {
                    if (this.modalShow) this.close();
                }
            });
        }

        @Watch('exportAreasDialog')
        private onPayloadChanged(value: any) {
            if (value) {
                this.areaSetId = null;
                this.filters = [];
                this.loadingProperties = false;
                this.includeValues = false;
                this.modalShow = true;
            } else {
                this.modalShow = false;
            }
        }

        @Watch('areaSetId')
        private async onAreaSetChanged(value: number | null) {
            this.filters = [];
            if (value != null && this.model) {
                this.loadingProperties = true;
                try {
                    await this.fetchExportAreasProperties({ modelId: this.model.id, areaSetId: value });
                } finally {
                    this.loadingProperties = false;
                }
            }
        }

        private get areaSetItems(): any[] {
            if (!this.model || !this.model.areaSets) return [];
            return this.model.areaSets.map((a: any) => ({ id: a.id, title: a.title || a.name }));
        }

        private get propertyItems(): any[] {
            return (this.getExportAreasProperties || []).map((p: any) => ({ name: p.name, title: p.title || p.name }));
        }

        private findProperty(name: string | null): any | null {
            return name ? (this.getExportAreasProperties || []).find((p: any) => p.name === name) || null : null;
        }

        // dataType: 1 = float, 2 = percentage, 3 = currency, 5 = boolean (everything else falls back to plain number).
        private formatPropertyValue(p: any, v: number | null | undefined): string {
            if (v === null || v === undefined || isNaN(Number(v))) return '';
            const digits = (p && typeof p.roundingDigits === 'number') ? p.roundingDigits : 2;
            const n = Number(v);
            if (p && p.dataType === 2) return (n * 100).toFixed(digits) + '%';
            return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
        }

        private propertyMinPlaceholder(name: string | null): string {
            const p = this.findProperty(name);
            return p && p.minValue != null ? this.formatPropertyValue(p, p.minValue) : '';
        }

        private propertyMaxPlaceholder(name: string | null): string {
            const p = this.findProperty(name);
            return p && p.maxValue != null ? this.formatPropertyValue(p, p.maxValue) : '';
        }

        private rangeHint(name: string | null): string {
            const p = this.findProperty(name);
            if (!p || (p.minValue == null && p.maxValue == null)) return '';
            return String(this.$t('message.exportAreasRangeHint', {
                min: this.formatPropertyValue(p, p.minValue),
                max: this.formatPropertyValue(p, p.maxValue),
            }));
        }

        private requiredRule(v: any): boolean | string {
            return (v != null && v !== '') || String(this.$t('message.required'));
        }

        private addFilter() {
            this.filters.push({ areaPropertyName: null, minValue: null, maxValue: null });
        }

        private removeFilter(idx: number) {
            this.filters.splice(idx, 1);
        }

        private buildFilters() {
            return this.filters
                .filter(f => !!f.areaPropertyName)
                .map(f => ({
                    areaPropertyName: f.areaPropertyName,
                    minValue: typeof f.minValue === 'number' && !isNaN(f.minValue) ? f.minValue : null,
                    maxValue: typeof f.maxValue === 'number' && !isNaN(f.maxValue) ? f.maxValue : null,
                }));
        }

        private async close() {
            const ok = await this.$confirm(String(this.$t('message.confirmClose')), {
                buttonTrueText: String(this.$t('message.yes')),
                buttonFalseText: String(this.$t('message.no')),
            });
            if (ok) this.$emit('cancel');
        }

        private async submit() {
            if (!this.model || this.areaSetId == null) return;
            const filters = this.buildFilters();

            // Server caps exports at 100k rows — block client-side so the user gets a clear error instead of a 400.
            const count = await this.previewExportAreasCount({ modelId: this.model.id, areaSetId: this.areaSetId, filters });
            if (count === null) return;
            if (count > 100000) {
                await this.$confirm(String(this.$t('message.exportAreasTooManyRows', { count })), {
                    color: 'error',
                    buttonTrueText: String(this.$t('message.close')),
                    buttonFalseText: null as any,
                });
                return;
            }

            const ok = await this.$confirm(String(this.$t('message.exportAreasRowCountConfirm', { count })), {
                buttonTrueText: String(this.$t('message.yes')),
                buttonFalseText: String(this.$t('message.no')),
            });
            if (!ok) return;

            this.$emit('save', { areaSetId: this.areaSetId, filters, includeValues: this.includeValues });
        }
    }
